<script setup lang="ts">
import { ref, useTemplateRef, watch, watchEffect } from 'vue';

import BuyBlock from '@/components/custom-trays/BuyBlock.vue';
import CatalogSort from '@/components/custom-trays/CatalogSort.vue';
import CategoryFilterControlPanel from '@/components/custom-trays/CategoryFilterControlPanel.vue';
import IngredientCatalog from '@/components/custom-trays/IngredientCatalog.vue';
import KeywordFilter from '@/components/custom-trays/KeywordFilter.vue';
import TagFilterControlPanel from '@/components/custom-trays/TagFilterControlPanel.vue';
import closeIcon from '@/icons/close';
import encoding from '@/utils/encoding';
import { isDefined } from '@/utils/isDefined';
import { CustomTraysProps } from '@/views/CustomTrays.vue';

const props = defineProps<{
  catalogCategoryTree: CustomTraysProps['catalogCategoryTree'];
  currentCode?: string;
  digitalGifting?: CustomTraysProps['digitalGifting'];
  ingredients: CustomTraysProps['ingredientCatalog'];
  isDigitalGifting: boolean;
  skus: CustomTraysProps['skus'];
  visible?: boolean;
}>();

const encodedIngredientIds = defineModel<string>('encodedIngredientIds');

defineEmits<{
  'hide-custom-tray-builder': [];
}>();

const activeIngredientId = ref<string>();
const activeFilters = ref<string[]>([]);
const activeKeyword = ref<string>();
const activeSlot = ref<number>();
const activeSort = ref<'popularity' | 'price'>('popularity');
const ingredientCatalogContainer = useTemplateRef('ingredientCatalogContainer');

const resetCatalogScrollTop = () => {
  ingredientCatalogContainer.value!.scrollTop = 0;
};
watch(activeFilters, resetCatalogScrollTop, { deep: true });
watch(activeKeyword, resetCatalogScrollTop);

const expanded = ref(false);
const toggleFilterAndSortBlock = () => {
  expanded.value = !expanded.value;
};

const quantity = ref(1);
const sku = ref(props.digitalGifting?.giftedTray?.sku ?? props.skus[0].externalId);
if (encodedIngredientIds.value?.endsWith('l') && sku.value !== props.skus[1].externalId) {
  sku.value = props.skus[1].externalId;
}

type MaybeIngredient = CustomTraysProps['ingredientCatalog'][number] | undefined;
const codeChunkSize = 3;
function contentsFromCode() {
  const content: MaybeIngredient[] = [undefined, undefined, undefined, undefined, undefined];
  if (!props.currentCode) {
    return content;
  }

  let code = props.currentCode;
  if (code.length > 15) {
    code = code.substring(0, 15);
  }

  for (let i = 0; i < code.length; i += codeChunkSize) {
    const skuId = `${encoding.sxgToNum(code.substring(i, i + codeChunkSize))}`;
    const ingredient = props.ingredients.find(({ id }) => id === skuId);
    content.splice(i / codeChunkSize, 1, ingredient);
  }

  return content;
}
const trayContents = ref<MaybeIngredient[]>(contentsFromCode());
watchEffect(() => {
  const code = ['000', '000', '000', '000', '000'];
  trayContents.value.forEach((ingredient, slot) => {
    if (ingredient) {
      code[slot] = `${encoding.numToSxg(ingredient.id, codeChunkSize)}`;
    }
  });

  if (sku.value === props.skus[1].externalId) {
    code.push('l');
  }

  encodedIngredientIds.value = code.join('');
});
watch([activeIngredientId, activeSlot], (updated) => {
  if (updated.every(isDefined)) {
    const ingredient = props.ingredients.find((i) => i.id === activeIngredientId.value);
    trayContents.value[activeSlot.value! - 1] = ingredient;
    activeIngredientId.value = undefined;
    activeSlot.value = undefined;
  }
});

const logo = nutshell['static_img/checkout/nuts-logo-checkout.png'];
</script>

<template>
  <div
    id="custom-tray-builder"
    class="h-full full-screen pl-15 pr-15"
    :class="{ hidden: !visible }"
    data-test="custom-tray-builder"
  >
    <div class="flex-row h-full gutters wrapped">
      <div
        class="flex w-full h-full min-w-0 px-4 ingredient-selection ws-md:w-2/3 pt-15"
        :class="{ 'hidden-xs hidden-sm': !activeSlot }"
      >
        <a
          aria-label="Close"
          class="close-overlay visible-xs visible-sm"
          data-test="close-ingredient-selection"
          href="#"
          v-html="closeIcon"
          @click.prevent="activeSlot = undefined"
        />
        <div class="w-full flex-column">
          <div class="flex">
            <h1
              v-if="isDigitalGifting"
              class="mr-8 transform -translate-y-2"
              aria-label="Nuts.com Digital Gift Tray Builder"
            >
              <RouterLink to="/">
                <img alt="Go to homepage" class="h-9 md:h-14 icon" :src="logo" />
              </RouterLink>
            </h1>
            <h1 class="visible-md visible-lg" data-test="build-your-own-tray">
              Build Your Own Tray
            </h1>
            <p v-if="isDigitalGifting" class="mt-2 mb-0 ml-8 visible-md visible-lg">
              Choose 5 of your favorite ingredients
            </p>
            <h1 class="visible-xs visible-sm">Select a Product</h1>
          </div>
          <div class="overflow-hidden vh-full">
            <div class="flex-row h-full gutters wrapped">
              <div class="w-full px-4 ws-md:w-1/4 ws-md:h-full lg:h-full">
                <div class="h-full overflow-auto" data-test="custom-tray-filters-block">
                  <CategoryFilterControlPanel
                    v-model="activeFilters"
                    :catalogCategoryTree
                    class="mb-15"
                  />
                  <div
                    class="mobile-container mb-15"
                    data-test="custom-tray-tag-filters-container"
                    :class="{ expanded }"
                  >
                    <h2
                      class="toggler visible-xs visible-sm"
                      data-test="custom-tray-filter-sort-toggler"
                      @click.prevent="toggleFilterAndSortBlock"
                    >
                      Filter &amp; Sort (<span>{{ activeFilters.length }}</span
                      >)
                    </h2>
                    <TagFilterControlPanel v-model="activeFilters" />
                    <CatalogSort v-model="activeSort" class="visible-xs" />
                  </div>
                </div>
              </div>
              <div class="w-full h-full px-4 ws-md:w-3/4">
                <div class="h-full flex-column" data-test="custom-tray-ingredient-catalog">
                  <div class="flex-row gutters wrapped mb-15">
                    <div class="w-full px-4 ws-md:w-1/2">
                      <KeywordFilter v-model="activeKeyword" />
                    </div>
                    <div class="w-full px-4 ws-md:w-1/2">
                      <CatalogSort v-model="activeSort" class="visible-md visible-lg" />
                    </div>
                  </div>
                  <div class="h-full overflow-auto" ref="ingredientCatalogContainer">
                    <IngredientCatalog
                      v-model:activeIngredientId="activeIngredientId"
                      :activeFilters
                      :activeKeyword
                      :activeSort
                      :ingredients
                      :showPrice="!isDigitalGifting"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        class="w-full h-full px-4 buy-block-container ws-md:w-1/3 pt-15"
        data-test="custom-tray-buy-block-container"
      >
        <div class="flex items-center justify-between">
          <div
            v-if="isDigitalGifting"
            class="md:hidden"
            aria-label="Nuts.com Digital Gift Tray Builder"
          >
            <RouterLink to="/">
              <img :src="logo" alt="Go to homepage" class="h-9 icon" />
            </RouterLink>
          </div>
          <h1 class="visible-xs visible-sm">
            Build Your {{ isDigitalGifting ? 'GiftDrop' : 'Own' }} Tray
          </h1>
        </div>
        <div
          class="tray-visualizer-instructions visible-xs visible-sm rounded-10"
          data-test="tray-visualizer-instructions"
        >
          <p>Click a section of the tray below<br />and fill it with something yummy!</p>
        </div>
        <div
          class="tray-visualizer-instructions visible-md visible-lg rounded-10"
          :class="{ hidden: !activeIngredientId }"
        >
          <p>Click on the section you want to fill!</p>
        </div>
        <a
          v-if="!isDigitalGifting"
          aria-label="Close"
          class="close-overlay"
          data-test="close-overlay"
          href="#"
          v-html="closeIcon"
          @click.prevent="$emit('hide-custom-tray-builder')"
        />
        <BuyBlock
          v-model:activeIngredientId="activeIngredientId"
          v-model:activeSlot="activeSlot"
          v-model:quantity="quantity"
          v-model:sku="sku"
          v-model:trayContents="trayContents"
          :digitalGifting
          :isDigitalGifting
          :skus
        />
      </div>
    </div>
  </div>
</template>
